import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
// We're still importing these for backward compatibility, but the component is now more generic
import { ContextMenuActions, GspContextMenuComponent } from '../context-menu/gsp-context-menu.component';

export interface SortMenuItem<T = any> {
    id: string;
    name: string;
    className?: string;
    attribute: keyof T;
    type?: 'date' | 'string';
    title?: boolean;
}

export interface SortChangeEvent<T = any> {
    sortMode: SortMenuItem<T>;
    direction: 'asc' | 'desc';
}

/**
 * A reusable sort menu component that can work with different data types.
 *
 * @example
 * // For task sorting:
 * <sort-menu<PanelTask>
 *    [menuItems]="taskSortItems"
 *    (sortChange)="onTaskSortChange($event)">
 * </sort-menu>
 *
 * // For job sorting:
 * <sort-menu<SurveyJob>
 *    [menuItems]="jobSortItems"
 *    (sortChange)="onJobSortChange($event)">
 * </sort-menu>
 *
 * @template T The type of data being sorted (e.g., PanelTask, SurveyJob)
 */
@Component({
    selector: 'sort-menu',
    templateUrl: './sort-menu.component.html',
    styles: ''
})
export class SortMenuComponent<T = any> implements OnInit, OnChanges {
    @Input() menuItems: SortMenuItem<T>[] = [];
    @Input() selectedSortOption: string;
    @Input() currentSortDirection: 'asc' | 'desc';
    @Input() disabled: boolean = false;
    @Input() resetAllToDefault: boolean = false;
    @Output() sortChange = new EventEmitter<SortChangeEvent<T>>();
    @Output() menuClosed = new EventEmitter<void>();
    @ViewChild('sortContextMenu') sortContextMenu: GspContextMenuComponent;
    // Icons for sorting
    dateIcons = {
        asc: 'icon_line_sort_arrow_down_24 i24',
        desc: 'icon_line_sort_arrow_up_24 i24'
    };
    nameIcons = {
        asc: 'icon_line_sort_name_asc_24 i24',
        desc: 'icon_line_sort_name_desc_24 i24'
    };

    selectedSortDirection: 'asc' | 'desc' = 'desc';
    contextMenuItems: ContextMenuActions[] = [];

    ngOnInit(): void {
        this.contextMenuItems = this.mapToContextMenuItems(this.menuItems);

        // Handle different scenarios for initializing the selected sort option
        if (this.menuItems && this.menuItems.length > 0) {
            if (!this.selectedSortOption) {
                // No input provided - find item with active-sort class
                const activeMenuItem = this.menuItems.find(
                    item => item.className && item.className.includes('active-sort')
                );
                if (activeMenuItem) {
                    this.selectedSortOption = activeMenuItem.id;
                    this.updateIconState(activeMenuItem.id, activeMenuItem);
                }
            } else {
                // Input provided - need to find the matching menu item by ID or enum value
                // The selectedSortOption could be a string enum name, numeric enum value, or string ID
                let selectedMenuItem = this.findMenuItemById(this.selectedSortOption);
                if (selectedMenuItem) {
                    // Set the initial sort direction from input if provided, otherwise default to 'desc'
                    if (this.currentSortDirection) {
                        this.selectedSortDirection = this.currentSortDirection;
                    }
                    // Make sure the internal state matches the input value
                    this.selectedSortOption = selectedMenuItem.id;
                    this.updateIconState(selectedMenuItem.id, selectedMenuItem);
                    this.applyActiveSortClass(selectedMenuItem.id);
                }
            }
        }
    }

    /**
     * Helper method to find a menu item by ID, considering different formats
     * This handles multiple cases:
     * 1. Direct string match: item.id === id
     * 2. String conversion match: String(item.id) === String(id)
     */
    private findMenuItemById(id: string): SortMenuItem<T> {
        // First try direct match
        let menuItem = this.menuItems.find(item => item.id === id);

        // If not found, try matching by string value conversion
        if (!menuItem) {
            menuItem = this.menuItems.find(item => String(item.id) === String(id));
        }

        return menuItem;
    }

    /**
     * Update the icon state for the selected item
     */
    private updateIconState(itemId: string, menuItem: SortMenuItem<T>): void {
        const contextMenuItem = this.contextMenuItems.find(
            item => item.id === itemId || String(item.id) === String(itemId)
        );

        if (contextMenuItem) {
            contextMenuItem.iconClass = this.getIconClass(menuItem, this.selectedSortDirection);
        }
    }

    /**
     * Apply the active-sort class to the selected item
     */
    private applyActiveSortClass(itemId: string): void {
        this.contextMenuItems.forEach(menuItem => {
            if (String(menuItem.id) === String(itemId)) {
                if (!menuItem.labelClass.includes('active-sort')) {
                    menuItem.labelClass += ' active-sort';
                }
            } else if (menuItem.labelClass.includes('active-sort')) {
                menuItem.labelClass = menuItem.labelClass.replace(' active-sort', '');
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['resetAllToDefault'] && this.resetAllToDefault) {
            this.contextMenuItems = this.mapToContextMenuItems(this.menuItems);
        }
    }

    mapToContextMenuItems(menuItems: SortMenuItem<T>[]): ContextMenuActions[] {
        return menuItems.map(item => ({
            id: item.id,
            name: item.name,
            title: false,
            labelClass: item.className || '',
            iconClass: item.type === 'date' ? this.dateIcons['desc'] : this.nameIcons['desc'],
            visible: () => true,
            execute: $event => this.onSelectingSort($event)
        }));
    }

    openContextMenu(event: MouseEvent) {
        if (this.disabled) return;
        this.sortContextMenu.onContextMenu(event, null);
    }

    /**
     * Handles the menuClosed event from the context menu
     * and emits the menuClosed event to parent components
     */
    onMenuClosed(): void {
        // Forward the menu closed event to parent components
        this.menuClosed.emit();
    }

    public onSelectingSort(item: ContextMenuActions) {
        if (this.disabled) return;

        // Always use String comparison for IDs to handle both string and enum values
        const isSame = String(item.id) === String(this.selectedSortOption);
        this.selectedSortOption = item.id;

        // Find the corresponding menu item
        const currentSortItem = this.findMenuItemById(item.id);
        if (!currentSortItem) return; // Safety check

        // Toggle direction if same, otherwise default to desc
        if (isSame) {
            this.selectedSortDirection = this.selectedSortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.selectedSortDirection = 'desc';
        }

        this.contextMenuItems.forEach(menuItem => {
            // Find the corresponding sort menu item to get its type
            const correspondingSortItem = this.findMenuItemById(menuItem.id);
            if (!correspondingSortItem) return; // Skip this item if no match found

            // Using String comparison for IDs
            if (String(menuItem.id) === String(item.id)) {
                if (!menuItem.labelClass.includes('active-sort')) {
                    menuItem.labelClass += ' active-sort';
                }
                // Always update the icon class for the selected item
                menuItem.iconClass = this.getIconClass(currentSortItem, this.selectedSortDirection);
            } else if (menuItem.labelClass.includes('active-sort')) {
                menuItem.labelClass = menuItem.labelClass.replace(' active-sort', '');
                // Use the corresponding sort item to get the correct icon type
                menuItem.iconClass = this.getIconClass(correspondingSortItem, 'desc');
            }
        });
        this.sortChange.emit({
            sortMode: currentSortItem,
            direction: this.selectedSortDirection
        });
    }

    private getIconClass(currentSortItem: SortMenuItem<T>, direction: 'asc' | 'desc'): string {
        const isDateType = currentSortItem.type === 'date';
        if (isDateType) {
            return direction === 'asc' ? this.dateIcons.asc : this.dateIcons.desc;
        } else {
            return direction === 'asc' ? this.nameIcons.asc : this.nameIcons.desc;
        }
    }

    isActive(item: SortMenuItem<T>): boolean {
        return item.id === this.selectedSortOption;
    }
}
